import { useCallback, useEffect, useState, useMemo } from "react";
import { useLogsApi } from "../../hooks/useLogsApi";
import { useResourceApi } from "../../hooks/useResourceApi";
import { Chip, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import CircleIcon from "@mui/icons-material/Circle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useRbac } from "../../hooks/RbacContext";
import { columnFilters as logsColumnFilters } from "./LogsFilterConfig";
import dayjs from "dayjs";
import { timestamp } from "rxjs";

export const useLogsViewModel = () => {
  const { fetchLogs, formatTimestamp } = useLogsApi();
  const { getResources } = useResourceApi();
  const { checkAccess } = useRbac();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [resources, setResources] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState("timestamp");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedLog, setSelectedLog] = useState(null);
  const [isViewLogOpen, setIsViewLogOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    operation: [],
    module: [],
    status: [],
    email_id: [],
    dateRange: null,
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        const result = await getResources();
        if (result.success) {
          setResources(result.data || []);
        }
      } catch (error) {
        console.error("Error fetching resources:", error);
      }
    };
    initializeData();
  }, []);

  const fetchLogsData = useCallback(async () => {
    try {
      setLoading(true);
      const schema =
        JSON.parse(localStorage.getItem("schemaInfo"))?.schema_name || "dev";

      const filterParams = {
        page,
        limit: rowsPerPage,
        sortField,
        sortOrder,
        schema,
        operation: filters.operation,
        module: filters.module,
        status: filters.status,
        email_id: filters.email_id,
        startDate: filters.dateRange?.[0]
          ? dayjs(filters.dateRange[0]).format("YYYY-MM-DD")
          : undefined,
        endDate: filters.dateRange?.[1]
          ? dayjs(filters.dateRange[1]).format("YYYY-MM-DD")
          : undefined,
      };

      const result = await fetchLogs(filterParams);

      if (result.success) {
        setLogs(result.data || []);
        setTotalCount(result.total || 0);
        setHasMore(result.total > (page + 1) * rowsPerPage);
        setError(null);
      } else {
        setError(result.error);
        setLogs([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      setError(error.message);
      setLogs([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortField, sortOrder, filters]); 

  useEffect(() => {
    fetchLogsData();
  }, []);

  useEffect(() => {
    fetchLogsData();
  }, [page, rowsPerPage, sortField, sortOrder, filters]);

  const columnFilters = useMemo(() => {
    return logsColumnFilters.map((filter) => {
      if (filter.field === "email_id") {
        return {
          ...filter,
          options: resources.map((resource) => ({
            value: resource.username,
            label: resource.username,
          })),
          value: filters[filter.field],
          onChange: (value) => handleFilterChange(filter.field, value),
          multiple: true,
        };
      }
      return {
        ...filter,
        value: filters[filter.field],
        onChange: (value) => handleFilterChange(filter.field, value),
        multiple: true,
      };
    });
  }, [filters, resources]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0);
  }, []);

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const handleSortChange = useCallback((field, order) => {
    setSortField(field);
    setSortOrder(order);
    setPage(0);
  }, []);

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setIsViewLogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsViewLogOpen(false);
    setSelectedLog(null);
  };

  const getActions = useCallback((log) => {
    const actions = [];
    if (checkAccess("logs", "view")) {
      actions.push({
        icon: <VisibilityIcon fontSize="small" />,
        label: "View",
        onClick: handleViewLog,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    return actions;
  }, []);

  const columns = [
    {
      id: "module",
      label: "Module",
      align: "left",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {value}
        </Typography>
      ),
      sortable: true,
    },
    {
      id: "operation",
      label: "Operation",
      align: "left",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            fontWeight: 500,
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {value}
        </Typography>
      ),
      sortable: true,
    },
    {
      id: "timestamp",
      label: "Timestamp",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {formatTimestamp(value)}
        </Typography>
      ),
      sortable: true,
    },
    {
      id: "email_id",
      label: "Updated By",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {value || "-"}
        </Typography>
      ),
      sortable: true,
    },

    {
      id: "status",
      label: "Status",
      align: "left",
      render: (value) => (
        <Chip
          label={value}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor:
              value.toLowerCase() === "success" ? "#E8F5E9" : "#FFEBEE",
            color: value.toLowerCase() === "success" ? "#2E7D32" : "#D32F2F",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      ),
      searchValue: (value) => value.toLowerCase(),
      sortable: true,
    },
  ];

  const fixedColumns = {
    left: [],
    right: ["status", "actions"],
  };

  return {
    logs,
    loading,
    error,
    columns,
    columnFilters,
    fixedColumns,
    page,
    rowsPerPage,
    total: totalCount,
    sortField,
    sortOrder,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    getActions,
    selectedLog,
    isViewLogOpen,
    handleCloseDialog,
    refetch: fetchLogsData,
    serverSidePagination: true,
    hasMore,
    filters,
    handleFilterChange,
  };
};
